import * as React from "react";
import { Button, Space, Alert } from "antd";
import { CameraOutlined } from "@ant-design/icons";
import { useBarcodeScanner, BarcodeScanResult } from "../../hooks/useBarcodeScanner";

interface CameraScannerProps {
  onScan: (result: BarcodeScanResult) => void | Promise<void>;
  className?: string;
  style?: React.CSSProperties;
  onCameraStatusChange?: (status: 'idle' | 'initializing' | 'ready' | 'error') => void;
  buttonSize?: 'large' | 'middle' | 'small';
}

/**
 * Camera barcode scanner component
 *
 * Provides a reusable camera-based barcode scanning interface with:
 * - Camera device selection
 * - Video preview
 * - Start/stop controls
 * - Error handling
 *
 * @example
 * ```tsx
 * <CameraScanner
 *   onScan={(result) => console.log('Scanned:', result.rawValue)}
 * />
 * ```
 */
export const CameraScanner: React.FC<CameraScannerProps> = ({ onScan, className, style, onCameraStatusChange, buttonSize = 'large' }) => {
  const {
    cameraStatus,
    startCamera,
    stopCamera,
    scanBarcode,
    videoRef,
    selectRef,
    error: cameraError,
    isScanning,
  } = useBarcodeScanner(onScan);

  // Notify parent of camera status changes
  React.useEffect(() => {
    if (onCameraStatusChange) {
      onCameraStatusChange(cameraStatus);
    }
  }, [cameraStatus, onCameraStatusChange]);

  return (
    <div className={className} style={style}>
      {/* Camera preview and device selector */}
      {cameraStatus !== "idle" && (
        <div>
          <select ref={selectRef} style={{ width: "100%", marginBottom: 8 }} />
          <div style={{ overflow: "hidden", height: "25vh", marginBottom: 8, borderRadius: 4, border: "2px solid #d9d9d9" }}>
            <video
              ref={videoRef}
              style={{
                width: "100%",
                position: "relative",
                top: "50%",
                transform: "translate(0, -50%)",
              }}
              muted
              autoPlay
              playsInline
            />
          </div>
        </div>
      )}

      {/* Error display */}
      {cameraError && (
        <Alert
          message={cameraError}
          type="error"
          closable
          onClose={() => stopCamera()}
          style={{ marginBottom: 8 }}
        />
      )}

      {/* Camera controls */}
      {cameraStatus === "ready" ? (
        <Space style={{ width: "100%", display: "flex" }}>
          <Button
            size={buttonSize}
            onClick={scanBarcode}
            type="primary"
            icon={<CameraOutlined />}
            loading={isScanning}
            disabled={isScanning}
            style={{ flex: 1, height: buttonSize === 'large' ? 48 : undefined }}
          >
            {isScanning ? "Snímam..." : "Prečítať kód"}
          </Button>
          <Button
            size={buttonSize}
            onClick={stopCamera}
            disabled={isScanning}
            style={{ flex: "0 0 auto", height: buttonSize === 'large' ? 48 : undefined }}
          >
            Zatvoriť kameru
          </Button>
        </Space>
      ) : (
        <Button
          htmlType="button"
          size={buttonSize}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            startCamera().catch(() => {
              // Error is handled by the hook
            });
          }}
          icon={<CameraOutlined />}
          loading={cameraStatus === "initializing"}
          style={{
            flex: "0 0 auto",
            height: buttonSize === 'large' ? 48 : undefined,
            width: buttonSize === 'large' ? 48 : undefined,
          }}
        />
      )}
    </div>
  );
};
